/**
 * Orgs API Client
 *
 * Provides interface and real implementation for listing the organizations a
 * **staff admin** may select and manage, via the tenant-management backend.
 *
 * The org list is backend-served, NOT derived from the v1 claim: a global
 * `platform.staff.admin` role can't enumerate every org from token grants
 * alone. The backend resolves the set by acting as the calling user against the
 * ZITADEL Admin API.
 *
 * Endpoints:
 * - GET /api/tenant-management/orgs - List orgs the staff user may manage
 *
 * The /api/tenant-management/ service prefix lets the Envoy/Gateway HTTPRoute
 * route all tenant-management traffic to the correct upstream without ambiguity
 * against other /api/* backends. Requests carry credentials:'include' and the
 * X-MP-Token-Handler CSRF header (added for /api/* paths by the http module).
 *
 * @module api/OrgsApiClient
 */

import type { ListOrgsResponse } from '../types';
import { httpGet, type HttpOptions } from './http';

// =============================================================================
// Interface
// =============================================================================

/**
 * Interface for organization listing operations.
 *
 * The Real implementation makes actual HTTP calls; the Mock implementation
 * returns configurable fixtures for testing and development.
 */
export interface OrgsApiClient {
  /**
   * List the organizations the current staff user may select and manage.
   *
   * @returns The list of selectable organizations
   * @throws HttpError on API errors (e.g. 403 if the user is not staff)
   * @throws SessionExpiredError if authentication fails
   */
  listOrgs(): Promise<ListOrgsResponse>;
}

// =============================================================================
// Constants
// =============================================================================

/**
 * Endpoint for listing selectable organizations.
 *
 * The /api/tenant-management/ service prefix is required so the proxy
 * HTTPRoute can route these requests to the tenant-management backend without
 * ambiguity against other /api/* upstreams.
 */
const ORGS_ENDPOINT = '/api/tenant-management/orgs';

// =============================================================================
// Real Implementation
// =============================================================================

/**
 * Real implementation of OrgsApiClient that calls the tenant-management
 * backend at /api/tenant-management/orgs.
 *
 * @example
 * ```typescript
 * const orgsClient = new RealOrgsApiClient();
 * const { orgs } = await orgsClient.listOrgs();
 * ```
 */
export class RealOrgsApiClient implements OrgsApiClient {
  private readonly options?: HttpOptions;

  /**
   * Create a new RealOrgsApiClient.
   *
   * @param options - Optional HTTP options (e.g., custom fetch for testing)
   */
  constructor(options?: HttpOptions) {
    this.options = options;
  }

  /**
   * GET /api/tenant-management/orgs
   *
   * Returns the backend-resolved set of orgs the staff user may manage.
   */
  async listOrgs(): Promise<ListOrgsResponse> {
    return httpGet<ListOrgsResponse>(ORGS_ENDPOINT, this.options);
  }
}

// =============================================================================
// Default Instance
// =============================================================================

/**
 * Default singleton instance of the Real Orgs API client.
 *
 * In most cases, import via the registry (./registry.ts) which selects
 * Real vs Mock based on BACKEND_READY and VITE_USE_MOCKS.
 */
export const orgsApiClient = new RealOrgsApiClient();
